import React, { FunctionComponent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Dropdown, DropdownItem } from 'plume-ui';
import { Maybe } from '../../../../../../../types';
import {
  FrequencyUnits,
  isTriggerTypeAvailable,
  possibleFrequencyUnits,
} from '../../../helpers';
import {
  OutreachConfigGenericFormField,
  OutreachConfigSections,
  OutreachTriggerTypes,
  SubscriberOutreachTriggerFormFields,
} from '../../../../../types';
import useFormControls from '../../../hooks/useFormControls';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import OutreachConfigSection from '../../../OutreachConfigSection';
import { outreachMetadataAtom } from '../../../../../../../state';
import { useRecoilValue } from 'recoil';
import { OutreachTypes } from '../../../../../../../urls';
import useValidation from '../../../hooks/useValidation';
import { useFeaturePermission } from '../../../../../../../hooks/useFeaturePermission';
import { Permission } from '../../../../../../../userPermissionConfig';
import { mapTriggerTypeToRenderer } from '../support/helpers';

export type FormikSubscriberOutreachTriggerValue = {
  triggerType: OutreachTriggerTypes;
  isOneTimeOnly: boolean;
  timeRepeatEvery: Maybe<number>;
  timeRepeatEveryUnit: FrequencyUnits;
  timeRepeatWeekdays?: string[];
  scheduleMaxFrequencyOfSupportOutreach: Maybe<number>;
  scheduleMaxFrequencyOfSupportOutreachEvery: Maybe<number>;
  scheduleMaxFrequencyOfSupportOutreachUnit: FrequencyUnits;
  sendClearEmailWhenIssueCleared: boolean;
  issueTriggeredWhen: Maybe<number>;
  issueClearedWhen: Maybe<number>;
};

type DropdownProperties =
  | SubscriberOutreachTriggerFormFields.TriggerType
  | SubscriberOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit
  | SubscriberOutreachTriggerFormFields.TimeRepeatEveryUnit;

type DropdownProperty = { id: string; labelId: string };
const dropdownsPossibleValues: Record<
  DropdownProperties,
  DropdownProperty[]
> = {
  [SubscriberOutreachTriggerFormFields.TriggerType]: [
    {
      id: OutreachTriggerTypes.Time,
      labelId: 'outreach.form.common.triggerTypes.time',
    },
    {
      id: OutreachTriggerTypes.MaxValue,
      labelId: 'outreach.form.common.triggerTypes.maxValue',
    },
    {
      id: OutreachTriggerTypes.MaxPercentage,
      labelId: 'outreach.form.common.triggerTypes.maxPercentage',
    },
    {
      id: OutreachTriggerTypes.MaxIncreaseRate,
      labelId: 'outreach.form.common.triggerTypes.maxIncreaseRate',
    },
    {
      id: OutreachTriggerTypes.CSPOutageDetected,
      labelId: 'outreach.form.common.triggerTypes.cspOutageDetected',
    },
  ],
  [SubscriberOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit]: possibleFrequencyUnits,
  [SubscriberOutreachTriggerFormFields.TimeRepeatEveryUnit]: possibleFrequencyUnits,
};

const FormikSubscriberOutreachTriggerField: FunctionComponent<OutreachConfigGenericFormField<
  FormikSubscriberOutreachTriggerValue
>> = ({ field, form }) => {
  const outreachMetadata = useRecoilValue(outreachMetadataAtom);
  const { t } = useTranslation();
  const {
    setPropertyValue,
    toggleFieldValue,
    getPropertyValue,
  } = useFormControls<FormikSubscriberOutreachTriggerValue>(field, form);
  const titleCopyId = 'outreach.form.subscriberOutreachTriggerSection.title';
  const subtitleCopyId =
    'outreach.form.subscriberOutreachTriggerSection.subtitle';
  const { isSectionComplete } = useValidation();
  const { hasPermission } = useFeaturePermission();
  const canEditSubscriberPush = hasPermission(
    Permission.EditSubscriberPushOutreach,
  );

  const retrieveAllowedDropdownOptionsFromMetadata = (
    property: DropdownProperties,
  ) => {
    let allowedValuesFromMetadata;
    let values = dropdownsPossibleValues[property];
    const triggerType =
      field.value[SubscriberOutreachTriggerFormFields.TriggerType];
    const outreachPushMetadata = outreachMetadata![OutreachTypes.Push];
    if (
      triggerType === OutreachTriggerTypes.Time &&
      [
        SubscriberOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit,
        SubscriberOutreachTriggerFormFields.TimeRepeatEveryUnit,
      ].includes(property)
    ) {
      allowedValuesFromMetadata =
        property === SubscriberOutreachTriggerFormFields.TimeRepeatEveryUnit
          ? outreachPushMetadata.time.allowedInputs.schedule?.allowedInputs
              ?.recurrenceUnit.allowedValues
          : outreachPushMetadata.maxFrequency.allowedInputs.recurrenceUnit
              .allowedValues;
    } else if (
      triggerType !== OutreachTriggerTypes.Time &&
      property ===
        SubscriberOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit
    ) {
      allowedValuesFromMetadata =
        outreachPushMetadata.maxFrequency.allowedInputs.recurrenceUnit
          .allowedValues;
    }

    if (
      allowedValuesFromMetadata &&
      [
        SubscriberOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit,
        SubscriberOutreachTriggerFormFields.TimeRepeatEveryUnit,
      ].includes(property)
    ) {
      const allowedValuesKeys = allowedValuesFromMetadata.map(
        (v: any) => v.key,
      );
      values = values.filter((v) => allowedValuesKeys.includes(v.id));
    }

    if (property === SubscriberOutreachTriggerFormFields.TriggerType) {
      values = values.filter(
        (v: DropdownProperty) =>
          outreachMetadata &&
          isTriggerTypeAvailable(
            v.id as OutreachTriggerTypes,
            outreachMetadata,
            OutreachTypes.Push,
          ),
      );
    }

    if (
      [
        SubscriberOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit,
        SubscriberOutreachTriggerFormFields.TimeRepeatEveryUnit,
      ].includes(property)
    ) {
      // removing Once
      values = values.filter(
        (v: DropdownProperty) => v.id !== FrequencyUnits.Once,
      );
    }

    return values;
  };

  function renderDropdownFor<
    T extends FormikSubscriberOutreachTriggerValue[keyof FormikSubscriberOutreachTriggerValue]
  >(property: DropdownProperties) {
    const possibleValues = retrieveAllowedDropdownOptionsFromMetadata(property);

    const value = field.value[property];
    let label: string = possibleValues[0].labelId;
    if (possibleValues.map(({ id }) => id).includes(value)) {
      label = possibleValues.find(({ id }) => id === value)?.labelId || label;
    } else {
      label = possibleValues[0].labelId;
      setPropertyValue(property, possibleValues[0].id as T);
    }

    return (
      <Dropdown
        label={t(label)}
        expandDirection="auto"
        openInPortal
        closeOnItemClick
      >
        {possibleValues.map((value) => (
          <DropdownItem
            key={value.id}
            onClick={() => setPropertyValue(property, value.id as T)}
          >
            <FormattedMessage id={value.labelId} />
          </DropdownItem>
        ))}
      </Dropdown>
    );
  }

  const renderTriggerContent = () => {
    const triggerType =
      field.value[SubscriberOutreachTriggerFormFields.TriggerType];
    const props = {
      form,
      field,
      renderDropdownFor,
      triggerType,
      name: field.name,
      outreachType: OutreachTypes.Push,
    };
    const Renderer = mapTriggerTypeToRenderer[triggerType] || React.Fragment;
    return <Renderer {...props} />;
  };

  const shouldDisplayOneTimeOnlyCheckbox =
    field.value![SubscriberOutreachTriggerFormFields.TriggerType] ===
    OutreachTriggerTypes.Time;

  const isComplete = isSectionComplete(
    OutreachConfigSections.SubscriberOutreachTrigger,
  );

  return (
    <OutreachConfigSection
      title={t(titleCopyId)}
      subtitle={t(subtitleCopyId)}
      isRequired
      isComplete={isComplete}
      isOpen={canEditSubscriberPush}
    >
      <div className="OutreachContainer__section-content-wrapper">
        <div className="OutreachContainer__subscriber-outreach-trigger-row">
          <div className="OutreachContainer__subscriber-outreach-trigger-label">
            <span className="color-required">* </span>
            <FormattedMessage id="outreach.form.supportOutreachTriggerSection.triggerType" />
          </div>
          <div className="OutreachContainer__subscriber-outreach-trigger-field OutreachContainer__subscriber-outreach-trigger-field--trigger-type">
            {renderDropdownFor(SubscriberOutreachTriggerFormFields.TriggerType)}
            {shouldDisplayOneTimeOnlyCheckbox && (
              <Checkbox
                onChange={() =>
                  toggleFieldValue(
                    SubscriberOutreachTriggerFormFields.IsOneTimeOnly,
                  )
                }
                checked={
                  getPropertyValue(
                    SubscriberOutreachTriggerFormFields.IsOneTimeOnly,
                  ) as boolean
                }
                label={t(
                  'outreach.form.supportOutreachTriggerSection.oneTimeOnly',
                )}
              />
            )}
          </div>
        </div>
        {renderTriggerContent()}
      </div>
    </OutreachConfigSection>
  );
};

export default memo(FormikSubscriberOutreachTriggerField);
